import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ImagePlus, Sparkles, Loader2, Rocket, Heart, X, MessageCircle, BadgeCheck, Trash2 } from "lucide-react";

function PostTile({ post, index, onOpen }) {
  const featured = post.boosted || index % 6 === 0;
  return (
    <button
      onClick={() => onOpen(post)}
      className={`relative ${featured ? "col-span-2 row-span-2" : "col-span-1 row-span-1"} group overflow-hidden bg-[#0A0B0D] border-2 border-ink focus:outline-none focus:ring-4 focus:ring-[#14C8B0]`}
      data-testid={`my-post-tile-${post.id}`}
    >
      {post.media_type === "video" ? (
        <video src={post.media_url} muted loop playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
      ) : (
        <img src={post.media_url} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 inset-x-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between text-white">
        <span className="flex items-center gap-1 text-sm font-display font-bold">
          <Heart className="w-4 h-4" strokeWidth={2.5} fill={post.liked_by_me ? "currentColor" : "none"}/>
          {post.likes_count}
        </span>
      </div>
      {post.boosted && (
        <span className="absolute top-2 start-2 px-2 py-1 rounded-full bg-[#F9C349] border-2 border-ink shadow-nb-sm text-[10px] font-display font-black text-ink flex items-center gap-1">
          <Rocket className="w-3 h-3" strokeWidth={2.8}/>BOOSTED
        </span>
      )}
    </button>
  );
}

function PostModal({ post, currentUserId, onClose, onChanged, onDeleted }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      onChanged({ ...post, liked_by_me: data.liked, likes_count: post.likes_count + (data.liked ? 1 : -1) });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const boost = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/boost`);
      toast.success(t("postsPage.boostedToast", { n: data.coins_spent }));
      onChanged({ ...post, boosted: true });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(t("postsPage.deleteConfirm"))) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success(t("postsPage.deletedToast"));
      onDeleted(post.id);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="my-post-modal">
      <div className="relative bg-white border-2 border-ink rounded-2xl shadow-nb max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 end-3 z-10 w-9 h-9 rounded-full bg-white border-2 border-ink shadow-nb-sm flex items-center justify-center hover:-translate-y-0.5 transition-transform" data-testid="my-post-modal-close">
          <X className="w-5 h-5" strokeWidth={2.5}/>
        </button>
        <div className="grid md:grid-cols-[1.2fr_1fr]">
          <div className="bg-black flex items-center justify-center md:max-h-[92vh]">
            {post.media_type === "video" ? (
              <video src={post.media_url} controls autoPlay className="w-full max-h-[92vh] object-contain"/>
            ) : (
              <img src={post.media_url} alt={post.title} className="w-full max-h-[92vh] object-contain"/>
            )}
          </div>
          <div className="p-5 sm:p-6 flex flex-col">
            <button onClick={() => nav(`/u/${post.author_id}`)} className="flex items-center gap-3 mb-4 group/author">
              <div className="w-11 h-11 rounded-full bg-[#FFF1D6] ring-1 ring-black/5 border-2 border-ink flex items-center justify-center font-display font-bold overflow-hidden">
                {post.author?.avatar_url
                  ? <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover"/>
                  : post.author?.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 text-start">
                <div className="font-display font-extrabold flex items-center gap-1 truncate group-hover/author:underline">
                  {post.author?.name}
                  {post.author?.verified && <BadgeCheck className="w-4 h-4 text-[#14C8B0]" strokeWidth={2.5}/>}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#7C828D]">
                  {new Date(post.created_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>
            </button>
            {post.title && <h2 className="font-display font-black text-2xl tracking-tight leading-tight mb-2">{post.title}</h2>}
            {post.caption && <p className="text-[15px] text-[#0F1115] whitespace-pre-wrap leading-relaxed mb-4">{post.caption}</p>}
            {post.activity && (
              <button onClick={() => nav(`/activity/${post.activity_id}`)} className="tag-nb self-start mb-4">
                {t("postsPage.fromActivity")} <b className="ms-1">{post.activity.title}</b>
              </button>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-auto pt-4 border-t-2 border-ink/10">
              <button onClick={toggleLike} className={`btn-nb !py-2 !px-3 ${post.liked_by_me ? "btn-nb-coral" : ""}`}>
                <Heart className="w-4 h-4" strokeWidth={2.5} fill={post.liked_by_me ? "currentColor" : "none"}/>
                <span>{post.likes_count}</span>
              </button>
              <button onClick={() => nav(`/chat/with/${post.author_id}`)} className="btn-nb !py-2 !px-3">
                <MessageCircle className="w-4 h-4" strokeWidth={2.5}/>
              </button>
              {post.author_id === currentUserId && !post.boosted && (
                <button onClick={boost} disabled={busy} className="btn-nb btn-nb-warn !py-2 !px-3">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Rocket className="w-4 h-4" strokeWidth={2.5}/>}
                  {t("postsPage.boost10")}
                </button>
              )}
              {post.boosted && (
                <span className="tag-nb bg-gradient-to-r from-[#F9C349] to-[#FFB347] text-[#4A3000] border-transparent">
                  <Rocket className="w-3 h-3" strokeWidth={2.5}/> {t("postsPage.boosted")}
                </span>
              )}
              {(post.author_id === currentUserId || (user && user.role === "admin" && (user.is_master || user.email === "mustafahaidari03@gmail.com" || (user.admin_permissions || []).includes("reports") || (user.admin_permissions || []).includes("users")))) && (
                <button onClick={del} className="btn-nb !p-2 ms-auto">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5}/>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyPosts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const { userId: paramUserId } = useParams();
  const userId = paramUserId || user?.id;
  const isOwn = userId === user?.id;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPost, setOpenPost] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/posts/user/${userId}`)
      .then((r) => setPosts(r.data || []))
      .catch((e) => toast.error(fmtError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [userId]);

  const onChanged = (updated) => {
    setPosts((arr) => arr.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setOpenPost((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
  };
  const onDeleted = (id) => {
    setPosts((arr) => arr.filter((p) => p.id !== id));
    setOpenPost(null);
  };

  return (
    <div className="max-w-7xl mx-auto pt-4 pb-32">
      <header className="px-4 sm:px-6 lg:px-8 mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} className="btn-nb !p-2.5" data-testid="my-posts-back">
            <ArrowLeft className="w-5 h-5" strokeWidth={2.5}/>
          </button>
          <div>
            <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-[#8A8F98]">
              {t("myPosts.kicker", { defaultValue: "your gallery" })}
            </p>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {isOwn ? t("myPosts.title", { defaultValue: "My Posts" }) : t("myPosts.titleOther", { defaultValue: "Posts" })}
              {posts.length > 0 && (
                <span className="ms-3 font-mono font-bold text-base text-[#7C828D]">{posts.length}</span>
              )}
            </h1>
          </div>
        </div>
        {isOwn && (
          <Link to="/post/new" className="btn-nb btn-nb-primary shrink-0" data-testid="my-posts-new-btn">
            <ImagePlus className="w-4 h-4" strokeWidth={2.5}/> {t("postsPage.post")}
          </Link>
        )}
      </header>

      {loading && (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 auto-rows-[120px] sm:auto-rows-[160px] md:auto-rows-[180px] gap-[2px]">
          {[...Array(9)].map((_, i) => (
            <div key={i} className={`${i % 6 === 0 ? "col-span-2 row-span-2" : ""} bg-gradient-to-br from-[#E8E3D6] to-[#D7D2C5] animate-pulse`}/>
          ))}
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="mx-4 sm:mx-6 lg:mx-8 card-nb p-10 text-center" style={{ background: "linear-gradient(135deg, #ECE6FF, #D9CFFF)" }} data-testid="my-posts-empty">
          <Sparkles className="w-12 h-12 mx-auto mb-3" strokeWidth={2.25}/>
          <h3 className="font-display font-extrabold text-2xl mb-1 tracking-tight">
            {isOwn ? t("myPosts.empty", { defaultValue: "No posts yet" }) : t("myPosts.emptyOther", { defaultValue: "Nothing here yet" })}
          </h3>
          <p className="text-[#4A4F58] mb-4">
            {isOwn ? t("myPosts.emptyBody", { defaultValue: "Share moments from your activities — your gallery will live here." }) : t("myPosts.emptyBodyOther", { defaultValue: "This person hasn't posted yet." })}
          </p>
          {isOwn && (
            <Link to="/post/new" className="btn-nb btn-nb-primary inline-flex">
              <ImagePlus className="w-4 h-4" strokeWidth={2.5}/> {t("postsPage.createFirst")}
            </Link>
          )}
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div
          className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 auto-rows-[120px] sm:auto-rows-[160px] md:auto-rows-[180px] gap-[2px]"
          data-testid="my-posts-grid"
        >
          {posts.map((p, i) => (
            <PostTile key={p.id} post={p} index={i} onOpen={setOpenPost}/>
          ))}
        </div>
      )}

      {openPost && (
        <PostModal
          post={openPost}
          currentUserId={user?.id}
          onClose={() => setOpenPost(null)}
          onChanged={onChanged}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
